import { useEffect, useState } from "react";
import { Type, Minus, Plus, RotateCcw } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const STORAGE_KEY = "inphase_zoom_level";
const LEVELS = [100, 115, 130, 150, 175];
const DEFAULT_LEVEL = 100;

function readStoredZoom() {
    const raw = Number(localStorage.getItem(STORAGE_KEY));
    return LEVELS.includes(raw) ? raw : DEFAULT_LEVEL;
}

/** Vision-accessibility control — scales the app's root font-size (rem-based
 * Tailwind sizing follows along), persisted across sessions. Independent of
 * browser zoom so layout stays intact at larger sizes. */
export function AccessibilityZoom() {
    const [zoom, setZoom] = useState(readStoredZoom);

    useEffect(() => {
        document.documentElement.style.fontSize = `${(16 * zoom) / 100}px`;
        localStorage.setItem(STORAGE_KEY, String(zoom));
    }, [zoom]);

    const idx = LEVELS.indexOf(zoom);
    const zoomOut = () => setZoom(LEVELS[Math.max(0, idx - 1)]);
    const zoomIn = () => setZoom(LEVELS[Math.min(LEVELS.length - 1, idx + 1)]);
    const reset = () => setZoom(DEFAULT_LEVEL);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    data-testid="accessibility-zoom-trigger"
                    title="Text size for accessibility"
                    aria-label="Adjust text size"
                    className={`border border-border hover:border-primary hover:text-primary p-1.5 ${zoom !== DEFAULT_LEVEL ? "text-primary border-primary/50" : "text-muted-foreground"}`}
                >
                    <Type className="w-4 h-4" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-3" align="end" data-testid="accessibility-zoom-panel">
                <div className="text-xs font-mono text-muted-foreground mb-2">Text size</div>
                <div className="flex items-center justify-between gap-2">
                    <button
                        data-testid="accessibility-zoom-out"
                        onClick={zoomOut}
                        disabled={idx === 0}
                        title="Decrease text size"
                        aria-label="Decrease text size"
                        className="border border-border p-1.5 hover:border-primary hover:text-primary disabled:opacity-30"
                    >
                        <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span data-testid="accessibility-zoom-value" className="font-mono text-sm w-12 text-center">{zoom}%</span>
                    <button
                        data-testid="accessibility-zoom-in"
                        onClick={zoomIn}
                        disabled={idx === LEVELS.length - 1}
                        title="Increase text size"
                        aria-label="Increase text size"
                        className="border border-border p-1.5 hover:border-primary hover:text-primary disabled:opacity-30"
                    >
                        <Plus className="w-3.5 h-3.5" />
                    </button>
                </div>
                <button
                    data-testid="accessibility-zoom-reset"
                    onClick={reset}
                    disabled={zoom === DEFAULT_LEVEL}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 border border-border py-1.5"
                >
                    <RotateCcw className="w-3 h-3" /> Reset to default
                </button>
            </PopoverContent>
        </Popover>
    );
}
